import { makeStyles } from '@material-ui/core/styles';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import ImageValidator from 'components/ux/ImageValidator';
import insta_placeholder from 'assets/discovery/insta_placeholder.png';
import PostInsights from './PostInsights';
import { ui, fmtCount } from './insightUi';

/**
 * Horizontal, scroll-snapped rail of post cards, shared by every posts
 * section (popular / recent / commercial / reels). Accepts the raw post
 * objects and reads likes/comments from either shape the API returns
 * (`post.stat.likes` for most sections, bare `post.likes` for popular posts).
 */
function PostRail({ posts = [] }) {
  const classes = useStyles();
  if (!posts.length) return null;
  return (
    <div className={classes.rail}>
      {posts.map((post, i) => {
        const likes = post.stat?.likes ?? post.likes;
        const comments = post.stat?.comments ?? post.comments;
        return (
          <div className={classes.card} key={post.link || i}>
            <div
              className={classes.media}
              onClick={() => post.link && window.open(post.link, '_blank')}
              role="link"
              tabIndex={0}
            >
              <ImageValidator className={classes.mediaImg} url={post.thumbnail ? post.thumbnail : post.image}>
                <img alt="" src={insta_placeholder} className={classes.mediaImg} />
              </ImageValidator>
            </div>
            <div className={classes.body}>
              {post.text ? <p className={classes.caption}>{post.text}</p> : <p className={classes.caption}>&nbsp;</p>}
              <div className={classes.statsRow}>
                <span className={classes.stat}>
                  <FavoriteRoundedIcon className={classes.statIconLike} />
                  {fmtCount(likes)}
                </span>
                <span className={classes.stat}>
                  <ChatBubbleRoundedIcon className={classes.statIconComment} />
                  {fmtCount(comments)}
                </span>
              </div>
              <PostInsights insights={post.insights} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const useStyles = makeStyles(() => ({
  rail: {
    display: 'flex',
    gap: 16,
    overflowX: 'auto',
    padding: '4px 2px 12px',
    scrollSnapType: 'x proximity',
    '&::-webkit-scrollbar': { height: 8 },
    '&::-webkit-scrollbar-thumb': { background: '#D6DAE6', borderRadius: 999 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
  },
  card: {
    flex: '0 0 auto',
    width: 248,
    scrollSnapAlign: 'start',
    background: ui.card,
    border: `1px solid ${ui.border}`,
    borderRadius: 14,
    overflow: 'hidden',
    fontFamily: ui.font,
    transition: 'transform 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: ui.shadowHover,
    },
  },
  media: {
    height: 280,
    cursor: 'pointer',
    background: '#EEF0F6',
    overflow: 'hidden',
    display: 'block',
  },
  mediaImg: {
    width: '100%',
    height: 280,
    objectFit: 'cover',
    display: 'block',
  },
  body: {
    padding: '12px 14px 14px',
  },
  caption: {
    margin: 0,
    fontSize: 12.5,
    lineHeight: 1.45,
    color: ui.inkSoft,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    minHeight: 36,
  },
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginTop: 10,
  },
  stat: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 800,
    color: ui.ink,
  },
  statIconLike: {
    fontSize: '16px !important',
    color: '#F43F5E',
  },
  statIconComment: {
    fontSize: '15px !important',
    color: '#64748B',
  },
}));

export default PostRail;
